/**
 * Outbox: Persistent intent queue for list membership and follow writes
 *
 * Implements a "write outbox" pattern that:
 * - Survives page unload/refresh
 * - Preserves last-intent-wins semantics
 * - Enables offline-first writes
 * - Is scalable and deterministic
 *
 * Key principles:
 * - One pending intent per (userId, listId, mediaType, mediaId) for list membership
 * - One pending intent per (userId, targetUserId) for follows
 * - Idempotent: all writes use stable idempotency keys
 * - Replay on boot, reconnect, focus, and periodically
 */

import { get, set, createStore, type UseStore } from 'idb-keyval';

// =============================================================================
// SCHEMA VERSION - bump to auto-clear old outbox data on shape change
// =============================================================================

const OUTBOX_VERSION = 2;

// =============================================================================
// TYPES
// =============================================================================

/** List membership intent (adding/removing items from lists) */
export interface ListMembershipIntent {
  intentType: 'listMembership';
  id: string; // UUID v4
  userId: string;
  listId: string;
  listSlug?: string; // For default lists: 'watched' | 'watchlist' | 'favorites'
  isDefaultList: boolean;
  mediaId: number;
  mediaType: 'movie' | 'tv';
  mediaTitle: string;
  mediaPoster?: string;
  mediaYear?: number;
  desiredInList: boolean;
  idempotencyKey: string; // Stable per intent, used for server-side dedup
  itemId?: string; // Known item ID (for removal optimization)
  createdAtMs: number;
  updatedAtMs: number;
  attempts: number;
  lastError?: string;
  nextRetryAtMs?: number; // For exponential backoff
}

/** User follow intent (following/unfollowing users) */
export interface UserFollowIntent {
  intentType: 'userFollow';
  id: string; // UUID v4
  userId: string; // viewerId (the actor)
  targetUserId: string;
  desiredFollowing: boolean;
  idempotencyKey: string;
  createdAtMs: number;
  updatedAtMs: number;
  attempts: number;
  lastError: string | null;
  nextRetryAtMs: number | null;
}

/** List reorder move intent (single-item move with snapshot revision) */
export interface ListReorderMoveIntent {
  intentType: 'reorderMove';
  id: string; // UUID v4
  userId: string;
  watchlistId: string;
  itemId: string;
  insertBeforeId: string | null; // null = move to end
  snapshotRevision: number;
  idempotencyKey: string; // Required: global invariant for all intent types
  createdAtMs: number;
  updatedAtMs: number;
  attempts: number;
  lastError: string | null;
  nextRetryAtMs: number | null;
}

/** Discriminated union of all intent types */
export type OutboxIntent = ListMembershipIntent | UserFollowIntent | ListReorderMoveIntent;

export type OutboxIntentKey = string; // Format: `${listId}:${mediaType}:${mediaId}` or `follow:${targetUserId}` or `reorder:${watchlistId}:${itemId}`

// =============================================================================
// CONSTANTS
// =============================================================================

const OUTBOX_DB_NAME = 'filmple-outbox';
const OUTBOX_STORE_NAME = 'intents';

const MAX_ATTEMPTS = 10;
const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 60000;

// =============================================================================
// STORE SETUP
// =============================================================================

let outboxStore: UseStore | null = null;

function getOutboxStore(): UseStore {
  if (!outboxStore) {
    outboxStore = createStore(OUTBOX_DB_NAME, OUTBOX_STORE_NAME);
  }
  return outboxStore;
}

function getOutboxKey(userId: string): string {
  return `outbox:${userId}`;
}

function getVersionKey(userId: string): string {
  return `filmple-outbox-version:${userId}`;
}

function getIntentKey(listId: string, mediaType: string, mediaId: number): OutboxIntentKey {
  return `${listId}:${mediaType}:${mediaId}`;
}

function getFollowIntentKey(targetUserId: string): OutboxIntentKey {
  return `follow:${targetUserId}`;
}

function getReorderIntentKey(watchlistId: string, itemId: string): OutboxIntentKey {
  return `reorder:${watchlistId}:${itemId}`;
}

// =============================================================================
// VERSION CHECKING (auto-clear on schema change)
// =============================================================================

/**
 * Ensure outbox version matches current schema. If not, clear and reset.
 * Must be called before any outbox read/write operation.
 */
async function ensureOutboxVersion(userId: string): Promise<void> {
  const store = getOutboxStore();
  const versionKey = getVersionKey(userId);

  try {
    const storedVersion = await get<number>(versionKey, store);

    if (storedVersion !== OUTBOX_VERSION) {
      // Schema changed - clear old outbox data
      if (import.meta.env.DEV) {
        console.log(`[OUTBOX] Schema version mismatch (stored: ${storedVersion}, current: ${OUTBOX_VERSION}). Clearing outbox for user ${userId}`);
      }
      await set(getOutboxKey(userId), {}, store);
      await set(versionKey, OUTBOX_VERSION, store);
    }
  } catch (err) {
    console.error('[OUTBOX] Failed to check/set version:', err);
  }
}

/**
 * Validate intent has required fields. Returns false if intent should be dropped.
 */
function isValidIntent(intent: unknown): intent is OutboxIntent {
  if (!intent || typeof intent !== 'object') return false;

  const i = intent as Record<string, unknown>;

  // Must have common fields
  if (typeof i.id !== 'string' || typeof i.userId !== 'string') return false;
  if (typeof i.idempotencyKey !== 'string') return false;
  if (typeof i.createdAtMs !== 'number' || typeof i.updatedAtMs !== 'number') return false;
  if (typeof i.attempts !== 'number') return false;

  // Check by intentType (or assume listMembership for legacy intents)
  const intentType = i.intentType ?? 'listMembership';

  if (intentType === 'userFollow') {
    if (typeof i.targetUserId !== 'string') return false;
    if (typeof i.desiredFollowing !== 'boolean') return false;
  } else if (intentType === 'listMembership') {
    if (typeof i.listId !== 'string') return false;
    if (typeof i.mediaId !== 'number') return false;
    if (typeof i.mediaType !== 'string') return false;
    if (typeof i.desiredInList !== 'boolean') return false;
  } else if (intentType === 'reorderMove') {
    if (typeof i.watchlistId !== 'string') return false;
    if (typeof i.itemId !== 'string') return false;
    if (typeof i.snapshotRevision !== 'number') return false;
    // insertBeforeId may be null (move to end)
  } else {
    return false; // Unknown intent type
  }

  return true;
}

// =============================================================================
// CORE OPERATIONS
// =============================================================================

/**
 * Get all pending intents for a user
 * Validates schema version and filters out invalid intents
 */
export async function getOutboxIntents(userId: string): Promise<Map<OutboxIntentKey, OutboxIntent>> {
  try {
    // Ensure schema version is correct (auto-clear if not)
    await ensureOutboxVersion(userId);

    const store = getOutboxStore();
    const data = await get<Record<OutboxIntentKey, unknown>>(getOutboxKey(userId), store);

    if (!data) return new Map();

    const result = new Map<OutboxIntentKey, OutboxIntent>();
    const invalidKeys: string[] = [];

    for (const [key, rawIntent] of Object.entries(data)) {
      // Validate intent structure
      if (!isValidIntent(rawIntent)) {
        if (import.meta.env.DEV) {
          console.warn(`[OUTBOX] Dropping invalid intent with key: ${key}`, rawIntent);
        }
        invalidKeys.push(key);
        continue;
      }

      // Migrate legacy intents (add intentType if missing)
      const intent = rawIntent as OutboxIntent;
      if (!('intentType' in intent)) {
        (intent as ListMembershipIntent).intentType = 'listMembership';
      }

      result.set(key, intent);
    }

    // Remove invalid intents from storage
    if (invalidKeys.length > 0) {
      const cleanedData = { ...data };
      for (const key of invalidKeys) {
        delete cleanedData[key];
      }
      await set(getOutboxKey(userId), cleanedData, store);
    }

    return result;
  } catch (err) {
    console.error('[OUTBOX] Failed to read intents:', err);
    return new Map();
  }
}

/**
 * Enqueue a list membership intent (last-intent-wins compaction)
 * If an intent for the same (listId, mediaType, mediaId) exists, it's overwritten.
 */
export async function enqueueIntent(
  userId: string,
  params: {
    listId: string;
    listSlug?: string;
    isDefaultList: boolean;
    mediaId: number;
    mediaType: 'movie' | 'tv';
    mediaTitle: string;
    mediaPoster?: string;
    mediaYear?: number;
    desiredInList: boolean;
    itemId?: string;
  }
): Promise<ListMembershipIntent> {
  // Ensure schema version is correct first
  await ensureOutboxVersion(userId);

  const store = getOutboxStore();
  const key = getIntentKey(params.listId, params.mediaType, params.mediaId);

  const intents = await getOutboxIntents(userId);
  const existing = intents.get(key) as ListMembershipIntent | undefined;

  const now = Date.now();

  // Generate new idempotency key if this is a new intent or direction changed
  // Keep existing key if we're just updating the same direction (prevents duplicate server writes)
  const needsNewIdempotencyKey = !existing || existing.intentType !== 'listMembership' || existing.desiredInList !== params.desiredInList;

  const intent: ListMembershipIntent = {
    intentType: 'listMembership',
    id: existing?.id || crypto.randomUUID(),
    userId,
    listId: params.listId,
    listSlug: params.listSlug,
    isDefaultList: params.isDefaultList,
    mediaId: params.mediaId,
    mediaType: params.mediaType,
    mediaTitle: params.mediaTitle,
    mediaPoster: params.mediaPoster,
    mediaYear: params.mediaYear,
    desiredInList: params.desiredInList,
    idempotencyKey: needsNewIdempotencyKey ? crypto.randomUUID() : existing!.idempotencyKey,
    itemId: params.itemId || existing?.itemId,
    createdAtMs: existing?.createdAtMs || now,
    updatedAtMs: now,
    attempts: needsNewIdempotencyKey ? 0 : (existing?.attempts || 0), // Reset attempts if direction changed
    lastError: needsNewIdempotencyKey ? undefined : existing?.lastError,
    nextRetryAtMs: undefined, // Clear backoff on new write
  };

  intents.set(key, intent);

  try {
    await set(getOutboxKey(userId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to write intent:', err);
  }

  return intent;
}

/**
 * Enqueue a follow intent (last-intent-wins per target user)
 * If an intent for the same targetUserId exists, it's overwritten.
 */
export async function enqueueFollowIntent(
  viewerId: string,
  targetUserId: string,
  desiredFollowing: boolean
): Promise<UserFollowIntent> {
  // Ensure schema version is correct first
  await ensureOutboxVersion(viewerId);

  const store = getOutboxStore();
  const key = getFollowIntentKey(targetUserId);

  const intents = await getOutboxIntents(viewerId);
  const existing = intents.get(key) as UserFollowIntent | undefined;

  const now = Date.now();

  // Generate new idempotency key if this is a new intent or direction changed
  const needsNewIdempotencyKey = !existing || existing.intentType !== 'userFollow' || existing.desiredFollowing !== desiredFollowing;

  const intent: UserFollowIntent = {
    intentType: 'userFollow',
    id: needsNewIdempotencyKey ? crypto.randomUUID() : existing!.id,
    userId: viewerId,
    targetUserId,
    desiredFollowing,
    idempotencyKey: needsNewIdempotencyKey ? crypto.randomUUID() : existing!.idempotencyKey,
    createdAtMs: existing?.createdAtMs || now,
    updatedAtMs: now,
    attempts: needsNewIdempotencyKey ? 0 : (existing?.attempts || 0),
    lastError: needsNewIdempotencyKey ? null : (existing?.lastError ?? null),
    nextRetryAtMs: null, // Clear backoff on new write
  };

  intents.set(key, intent);

  try {
    await set(getOutboxKey(viewerId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to write follow intent:', err);
  }

  return intent;
}

/**
 * Update intent fields (union-safe)
 * itemId only applies to listMembership intents
 */
export async function updateIntent(
  userId: string,
  intentKey: OutboxIntentKey,
  updates: {
    attempts?: number;
    lastError?: string | null;
    nextRetryAtMs?: number | null;
    itemId?: string; // only for listMembership
    snapshotRevision?: number; // only for reorderMove
  }
): Promise<void> {
  const store = getOutboxStore();
  const intents = await getOutboxIntents(userId);
  const intent = intents.get(intentKey);

  if (!intent) return;

  // Build updated intent based on type
  if (intent.intentType === 'listMembership') {
    const updated: ListMembershipIntent = {
      ...intent,
      updatedAtMs: Date.now(),
    };
    if (updates.attempts !== undefined) updated.attempts = updates.attempts;
    if (updates.lastError !== undefined) updated.lastError = updates.lastError ?? undefined;
    if (updates.nextRetryAtMs !== undefined) updated.nextRetryAtMs = updates.nextRetryAtMs ?? undefined;
    if (updates.itemId !== undefined) updated.itemId = updates.itemId;
    intents.set(intentKey, updated);
  } else if (intent.intentType === 'reorderMove') {
    const updated: ListReorderMoveIntent = {
      ...intent,
      updatedAtMs: Date.now(),
    };
    if (updates.attempts !== undefined) updated.attempts = updates.attempts;
    if (updates.lastError !== undefined) updated.lastError = updates.lastError ?? null;
    if (updates.nextRetryAtMs !== undefined) updated.nextRetryAtMs = updates.nextRetryAtMs ?? null;
    if (updates.snapshotRevision !== undefined) updated.snapshotRevision = updates.snapshotRevision;
    intents.set(intentKey, updated);
  } else {
    // userFollow intent - no itemId
    const updated: UserFollowIntent = {
      ...intent,
      updatedAtMs: Date.now(),
    };
    if (updates.attempts !== undefined) updated.attempts = updates.attempts;
    if (updates.lastError !== undefined) updated.lastError = updates.lastError ?? null;
    if (updates.nextRetryAtMs !== undefined) updated.nextRetryAtMs = updates.nextRetryAtMs ?? null;
    intents.set(intentKey, updated);
  }

  try {
    await set(getOutboxKey(userId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to update intent:', err);
  }
}

/**
 * Remove a completed intent
 */
export async function removeIntent(userId: string, intentKey: OutboxIntentKey): Promise<void> {
  const store = getOutboxStore();
  const intents = await getOutboxIntents(userId);

  intents.delete(intentKey);

  try {
    await set(getOutboxKey(userId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to remove intent:', err);
  }
}

/**
 * Remove an intent only if its idempotencyKey matches the expected value.
 * Prevents Move 1's cleanup from deleting Move 2's overwritten intent.
 */
export async function removeIntentIfMatches(
  userId: string,
  intentKey: OutboxIntentKey,
  expectedIdempotencyKey: string
): Promise<void> {
  const store = getOutboxStore();
  const intents = await getOutboxIntents(userId);
  const intent = intents.get(intentKey);
  if (!intent || intent.idempotencyKey !== expectedIdempotencyKey) return;
  intents.delete(intentKey);
  try {
    await set(getOutboxKey(userId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to remove intent:', err);
  }
}

/**
 * Clear all intents for a user (e.g., on logout)
 */
export async function clearOutbox(userId: string): Promise<void> {
  const store = getOutboxStore();

  try {
    await set(getOutboxKey(userId), {}, store);
  } catch (err) {
    console.error('[OUTBOX] Failed to clear outbox:', err);
  }
}

/**
 * Get count of pending intents
 */
export async function getOutboxCount(userId: string): Promise<number> {
  const intents = await getOutboxIntents(userId);
  return intents.size;
}

/**
 * Calculate next retry delay using exponential backoff
 */
export function calculateRetryDelay(attempts: number): number {
  const delay = Math.min(BASE_RETRY_DELAY_MS * Math.pow(2, attempts), MAX_RETRY_DELAY_MS);
  // Add jitter (±20%)
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.round(delay + jitter);
}

/**
 * Check if intent should be retried now
 */
export function shouldRetryNow(intent: OutboxIntent): boolean {
  if (intent.attempts >= MAX_ATTEMPTS) {
    return false;
  }
  if (intent.nextRetryAtMs && Date.now() < intent.nextRetryAtMs) {
    return false;
  }
  return true;
}

/**
 * Get intents ready for replay (sorted by updatedAtMs asc for deterministic order)
 */
export async function getIntentsToReplay(userId: string): Promise<OutboxIntent[]> {
  const intents = await getOutboxIntents(userId);

  return Array.from(intents.values())
    .filter(shouldRetryNow)
    .sort((a, b) => a.updatedAtMs - b.updatedAtMs);
}

/**
 * Get list membership intents ready for replay
 */
export async function getListMembershipIntentsToReplay(userId: string): Promise<ListMembershipIntent[]> {
  const intents = await getOutboxIntents(userId);

  return Array.from(intents.values())
    .filter((intent): intent is ListMembershipIntent =>
      intent.intentType === 'listMembership' && shouldRetryNow(intent)
    )
    .sort((a, b) => a.updatedAtMs - b.updatedAtMs);
}

/**
 * Get follow intents ready for replay
 */
export async function getFollowIntentsToReplay(userId: string): Promise<UserFollowIntent[]> {
  const intents = await getOutboxIntents(userId);

  return Array.from(intents.values())
    .filter((intent): intent is UserFollowIntent =>
      intent.intentType === 'userFollow' && shouldRetryNow(intent)
    )
    .sort((a, b) => a.updatedAtMs - b.updatedAtMs);
}

/**
 * Get the follow intent key format (for external use)
 */
export function getFollowIntentKeyForTarget(targetUserId: string): OutboxIntentKey {
  return getFollowIntentKey(targetUserId);
}

/**
 * Get the reorder intent key format (for external use)
 */
export function getReorderIntentKeyForLookup(watchlistId: string, itemId: string): OutboxIntentKey {
  return getReorderIntentKey(watchlistId, itemId);
}

/**
 * Enqueue a reorder-move intent (last-intent-wins per watchlistId+itemId).
 * If an intent for the same (watchlistId, itemId) exists, it's overwritten.
 */
export async function enqueueReorderIntent(
  userId: string,
  params: {
    watchlistId: string;
    itemId: string;
    insertBeforeId: string | null;
    snapshotRevision: number;
  }
): Promise<ListReorderMoveIntent> {
  await ensureOutboxVersion(userId);

  const store = getOutboxStore();
  const key = getReorderIntentKey(params.watchlistId, params.itemId);

  const intents = await getOutboxIntents(userId);
  const existing = intents.get(key) as ListReorderMoveIntent | undefined;

  const now = Date.now();

  const intent: ListReorderMoveIntent = {
    intentType: 'reorderMove',
    id: existing?.id || crypto.randomUUID(),
    userId,
    watchlistId: params.watchlistId,
    itemId: params.itemId,
    insertBeforeId: params.insertBeforeId,
    snapshotRevision: params.snapshotRevision,
    idempotencyKey: crypto.randomUUID(),
    createdAtMs: existing?.createdAtMs || now,
    updatedAtMs: now,
    attempts: 0, // Always reset: new move supersedes old
    lastError: null,
    nextRetryAtMs: now + 10_000, // Delay replay to avoid racing the immediate in-tab request
  };

  intents.set(key, intent);

  try {
    await set(getOutboxKey(userId), Object.fromEntries(intents), store);
  } catch (err) {
    console.error('[OUTBOX] Failed to write reorder intent:', err);
  }

  return intent;
}

/**
 * Get reorder-move intents ready for replay
 */
export async function getReorderIntentsToReplay(userId: string): Promise<ListReorderMoveIntent[]> {
  const intents = await getOutboxIntents(userId);

  return Array.from(intents.values())
    .filter((intent): intent is ListReorderMoveIntent =>
      intent.intentType === 'reorderMove' && shouldRetryNow(intent)
    )
    .sort((a, b) => a.updatedAtMs - b.updatedAtMs);
}
